import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import { FaPlus, FaTrash, FaSearch, FaTimes, FaCalendarAlt, FaBox, FaEye, FaFilter, FaDownload, FaExchangeAlt, FaArrowRight, FaMapMarkerAlt, FaStickyNote, FaEdit, FaFileExport, FaHistory, FaChevronLeft, FaChevronRight, FaSave, FaFileExcel } from 'react-icons/fa';
import Select from 'react-select';
import StockItemSelectionModal from '../../components/StockItemSelectionModal';

const StockItemsLocationTransfer = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFromLocation, setSelectedFromLocation] = useState(null);
  const [selectedToLocation, setSelectedToLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState("");
  
  // Item state
  const [items, setItems] = useState([{
    stockItemId: null,
    stockItemName: '',
    stockItemDetails: null,
    freshQuantity: 0,
    damagedQuantity: 0,
    availableStock: { freshQuantity: 0, damagedQuantity: 0 }
  }]);

  // Stock Item selection modal state
  const [showStockItemsModal, setShowStockItemsModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  
  // Transfer history state
  const [transferHistory, setTransferHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    fromLocation: null,
    toLocation: null,
    startDate: null,
    endDate: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [viewMode, setViewMode] = useState('grid');

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFilters, setExportFilters] = useState(() => {
    const now = new Date();
    const fyYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    return {
      startDate: `${fyYear}-04-01`,
      endDate: now.toISOString().split('T')[0],
      location: null
    };
  });

  // Refs for focus management
  const freshQuantityRefs = useRef([]);
  const damagedQuantityRefs = useRef([]);
  const filterDateStartRef = useRef(null);
  const filterDateEndRef = useRef(null);

  // Initial data loading
  useEffect(() => {
    fetchStockItems();
    fetchLocations();
    fetchTransferHistory();
  }, []);
  
  // Load transfer history when filters or pagination changes
  useEffect(() => {
    fetchTransferHistory();
  }, [historyPage, historyLimit, historyFilters]);
  


  // Update refs when items change
  useEffect(() => {
    freshQuantityRefs.current = freshQuantityRefs.current.slice(0, items.length);
    damagedQuantityRefs.current = damagedQuantityRefs.current.slice(0, items.length);
  }, [items]);

  // Update available stock when from location changes
  useEffect(() => {
    if (selectedFromLocation) {
      const updateAvailableStock = async () => {
        const updatedItems = await Promise.all(
          items.map(async (item) => {
            if (item.stockItemId) {
              const stock = await fetchStockItemStock(item.stockItemId, selectedFromLocation.value);
              return { ...item, availableStock: stock };
            }
            return item;
          })
        );
        setItems(updatedItems);
      };
      updateAvailableStock();
    }
  }, [selectedFromLocation]);

  const fetchStockItems = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items?userId=${user.user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStockItems(Array.isArray(data) ? data : data.data || []);
      }
    } catch (error) {
      console.error('Error fetching stock items:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load stock items',
        icon: 'error'
      });
    }
  };

  const fetchLocations = async () => {
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`);
      const data = await response.json();
      setLocations(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load locations',
        icon: 'error'
      });
    }
  };
  
  const fetchTransferHistory = async () => {
    try {
      setHistoryLoading(true);
      const companyId = user?.user?.company?._id || user?.user?.company;
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        company: companyId,
        ...(historyFilters.fromLocation && { fromLocation: historyFilters.fromLocation }),
        ...(historyFilters.toLocation && { toLocation: historyFilters.toLocation }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate })
      });

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stock-items/location-transfers?${queryParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = response.data;

      if (data.success) {
        const transfersData = data.data || [];
        
        // Ensure location data is properly formatted
        const processedTransfers = transfersData.map(transfer => {
          return {
            ...transfer,
            fromLocation: transfer.fromLocation || {},
            toLocation: transfer.toLocation || {},
            // Add fallbacks for location names
            fromLocationName: transfer.fromLocationName || 
              transfer.fromLocation?.locationName || 
              transfer.fromLocation?.name || 
              "Unknown",
            toLocationName: transfer.toLocationName || 
              transfer.toLocation?.locationName || 
              transfer.toLocation?.name || 
              "Unknown"
          };
        });
        
        setTransferHistory(processedTransfers);
        setTotalHistory(data.pagination?.totalItems || 0);
        setTotalPages(data.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error('Error fetching transfer history:', error);
      setTransferHistory([]);
      setTotalHistory(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchStockItemStock = async (stockItemId, locationId) => {
    try {
      const stockItem = stockItems.find(s => s._id === stockItemId);
      if (!stockItem || !stockItem.stock) {
        return { freshQuantity: 0, damagedQuantity: 0 };
      }

      const locationStock = stockItem.stock.find(s => s.location._id === locationId || s.location === locationId);
      if (locationStock) {
        return {
          freshQuantity: locationStock.freshQuantity || 0,
          damagedQuantity: locationStock.damagedQuantity || 0
        };
      }
      
      return { freshQuantity: 0, damagedQuantity: 0 };
    } catch (error) {
      console.error('Error fetching stock item stock:', error);
      return { freshQuantity: 0, damagedQuantity: 0 };
    }
  };

  const handleStockItemSelect = async (stockItem) => {
    if (currentItemIndex !== null && selectedFromLocation) {
      // Check if item already exists
      const existingIndex = items.findIndex(item => item.stockItemId === stockItem._id);
      if (existingIndex !== -1 && existingIndex !== currentItemIndex) {
        Swal.fire({
          title: 'Item Already Added',
          text: 'This stock item is already in the list',
          icon: 'warning'
        });
        return;
      }

      // Get available stock for this stock item at selected from location
      const locationStock = await fetchStockItemStock(stockItem._id, selectedFromLocation.value);

      const updatedItems = [...items];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        stockItemId: stockItem._id,
        stockItemName: stockItem.name,
        stockItemDetails: stockItem,
        availableStock: locationStock
      };
      setItems(updatedItems);
      setShowStockItemsModal(false);
      setCurrentItemIndex(null);

      // Focus on fresh quantity for the selected item
      setTimeout(() => {
        freshQuantityRefs.current[currentItemIndex]?.focus();
      }, 100);
    } else if (!selectedFromLocation) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Please select a "From Location" first',
        icon: 'warning'
      });
    }
  };

  const handleStockChange = (index, field, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    const item = items[index];
    
    // Check if trying to exceed available stock
    if (item.availableStock) {
      const maxValue = item.availableStock[field] || 0;
      if (numValue > maxValue) {
        Swal.fire({
          title: 'Stock Limit Exceeded',
          text: `Cannot exceed available stock (${maxValue})`,
          icon: 'warning'
        });
        return;
      }
    }

    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: numValue
    };
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([...items, {
      stockItemId: null,
      stockItemName: '',
      stockItemDetails: null,
      freshQuantity: 0,
      damagedQuantity: 0,
      availableStock: { freshQuantity: 0, damagedQuantity: 0 }
    }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
    }
  };

  // Enhanced keyboard controls
  const handleKeyDown = (e, index, field) => {
    const key = e.key;
    
    // Tab and Enter navigation
    if (key === 'Enter' || key === 'Tab') {
      e.preventDefault();
      
      if (field === 'freshQuantity') {
        // Move to damaged quantity of current row
        damagedQuantityRefs.current[index]?.focus();
      } else if (field === 'damagedQuantity') {
        // Move to next row fresh quantity, or add new row if this is the last one
        if (index === items.length - 1) {
          handleAddItem();
          setTimeout(() => {
            freshQuantityRefs.current[index + 1]?.focus();
          }, 0);
        } else {
          freshQuantityRefs.current[index + 1]?.focus();
        }
      }
    }
  };

  const validateForm = () => {
    if (!selectedFromLocation) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select a "From Location"',
        icon: 'warning'
      });
      return false;
    }
    if (!selectedToLocation) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select a "To Location"',
        icon: 'warning'
      });
      return false;
    }
    if (selectedFromLocation.value === selectedToLocation.value) {
      Swal.fire({
        title: 'Validation Error',
        text: 'From and To locations cannot be the same',
        icon: 'warning'
      });
      return false;
    }
    if (!selectedDate) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select a transfer date',
        icon: 'warning'
      });
      return false;
    }

    const validItems = items.filter(item => 
      item.stockItemId && (item.freshQuantity > 0 || item.damagedQuantity > 0)
    );

    if (validItems.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please add at least one item with transfer quantity',
        icon: 'warning'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      const validItems = items
        .filter(item => item.stockItemId && (item.freshQuantity > 0 || item.damagedQuantity > 0))
        .map(item => ({
          stockItemId: item.stockItemId,
          freshQuantity: item.freshQuantity,
          damagedQuantity: item.damagedQuantity
        }));

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items/location-transfers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          items: validItems,
          fromLocation: selectedFromLocation.value,
          toLocation: selectedToLocation.value,
          transferDate: selectedDate instanceof Date ? selectedDate.toISOString().split('T')[0] : selectedDate,
          remarks,
          createdBy: user.user._id
        })
      });

      const result = await response.json();
      
      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Location transfer created successfully!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Reset form
        setItems([{
          stockItemId: null,
          stockItemName: '',
          stockItemDetails: null,
          freshQuantity: 0,
          damagedQuantity: 0,
          availableStock: { freshQuantity: 0, damagedQuantity: 0 }
        }]);
        setSelectedFromLocation(null);
        setSelectedToLocation(null);
        setSelectedDate(new Date().toISOString().split('T')[0]);
        setRemarks("");
        
        // Refresh history and stock items
        fetchTransferHistory();
        fetchStockItems();
        
        // Switch to history tab
        setActiveTab('history');
      } else {
        Swal.fire({
          title: 'Error',
          text: result.message || 'Failed to create location transfer',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating location transfer:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to create location transfer',
        icon: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({
      fromLocation: null,
      toLocation: null,
      startDate: null,
      endDate: null
    });
    setHistoryPage(1);
  };



  // Open stock item selection with enhanced focus
  const openStockItemSelectionModal = (index) => {
    if (!selectedFromLocation) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Please select a "From Location" first',
        icon: 'warning'
      });
      return;
    }
    setCurrentItemIndex(index);
    setShowStockItemsModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateTotalQuantity = (items) => {
    return items.reduce((total, item) => {
      return total + (item.freshQuantity || 0) + (item.damagedQuantity || 0);
    }, 0);
  };

  const handleViewDetails = (transfer) => {
    setSelectedTransfer(transfer);
    setShowDetailsModal(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      [
        'Transfer ID', 'Date', 'From Location', 'To Location', 'Stock Item', 'Stock Item ID', 'Brand', 'Category', 
        'Unit of Measure', 'Fresh Quantity', 'Damaged Quantity', 'Total Quantity', 'Created By', 'Created By Email', 'Remarks'
      ],
      ...transferHistory.flatMap(transfer => 
        transfer.items.map(item => [
          transfer.transferId,
          formatDate(transfer.transferDate),
          transfer.fromLocationName,
          transfer.toLocationName,
          item.stockItemId?.name || 'N/A',
          item.stockItemId?.id || 'N/A',
          item.stockItemId?.brand?.name || 'No Brand',
          item.stockItemId?.category?.name || 'No Category',
          item.stockItemId?.unitOfMeasure || 'N/A',
          item.freshQuantity || 0,
          item.damagedQuantity || 0,
          (item.freshQuantity || 0) + (item.damagedQuantity || 0),
          transfer.createdByName || transfer.createdBy?.name || 'Unknown',
          transfer.createdBy?.email || 'N/A',
          transfer.remarks || ''
        ])
      )
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-items-location-transfers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const companyId = user?.user?.company?._id || user?.user?.company;
      const params = new URLSearchParams({
        startDate: exportFilters.startDate,
        endDate: exportFilters.endDate,
        company: companyId
      });
      if (exportFilters.location) {
        params.append('location', exportFilters.location);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/stock-items/location-transfers/export-excel?${params}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Location_Transfers_${exportFilters.startDate}_to_${exportFilters.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      Swal.fire({ title: 'Success', text: 'Location transfers exported successfully', icon: 'success', timer: 2000, showConfirmButton: false });
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting location transfers:', error);
      Swal.fire('Error', 'Failed to export location transfers', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  // React Select dark mode styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      fontSize: '14px',
      backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
      borderColor: darkMode ? (state.isFocused ? '#3b82f6' : '#4b5563') : (state.isFocused ? '#3b82f6' : '#d1d5db'),
      color: darkMode ? '#f3f4f6' : base.color,
      '&:hover': { borderColor: darkMode ? '#6b7280' : '#9ca3af' },
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.3)' : base.boxShadow,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
      border: darkMode ? '1px solid #4b5563' : base.border,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: darkMode
        ? state.isSelected ? '#3b82f6' : state.isFocused ? '#374151' : '#1f2937'
        : state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : base.backgroundColor,
      color: darkMode ? '#f3f4f6' : state.isSelected ? '#fff' : base.color,
      '&:active': { backgroundColor: darkMode ? '#4b5563' : '#dbeafe' },
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : base.color,
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : base.color,
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : base.color,
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#4b5563' : base.backgroundColor,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : base.color,
      '&:hover': { color: darkMode ? '#d1d5db' : '#6b7280' },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : base.color,
      '&:hover': { color: darkMode ? '#f87171' : '#ef4444' },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : base.color,
    }),
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 flex items-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            <FaExchangeAlt className="mr-3 text-blue-600" />
            Stock Items Location Transfer
          </h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Transfer stock items between locations</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className={`flex space-x-8 ${darkMode ? 'border-b border-gray-700' : ''}`} aria-label="Tabs">
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaPlus className="mr-2" />
              Create Transfer
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaHistory className="mr-2" />
              Transfer History ({totalHistory})
            </button>
          </nav>
        </div>

        {/* Create Transfer Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaMapMarkerAlt className="inline mr-1" />
                    From Location *
                  </label>
                  <Select
                    value={selectedFromLocation}
                    onChange={setSelectedFromLocation}
                    options={locations.map(location => ({
                      value: location._id,
                      label: location.name || location.locationName
                    }))}
                    placeholder="Select from location"
                    isClearable
                    className="text-sm"
                    styles={selectStyles}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaArrowRight className="inline mr-1" />
                    To Location *
                  </label>
                  <Select
                    value={selectedToLocation}
                    onChange={setSelectedToLocation}
                    options={locations
                      .filter(location => location._id !== selectedFromLocation?.value)
                      .map(location => ({
                        value: location._id,
                        label: location.name || location.locationName
                      }))}
                    placeholder="Select to location"
                    isClearable
                    className="text-sm"
                    styles={selectStyles}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaCalendarAlt className="inline mr-1" />
                    Transfer Date *
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'border-gray-300'}`}
                    required
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className={`min-w-full table-auto border-collapse ${darkMode ? 'border-gray-600' : ''}`}>
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Stock Item
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaBox className="inline mr-1" />
                        Fresh Qty
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaBox className="inline mr-1" />
                        Damaged Qty
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Available Stock
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div
                            className={`flex items-center justify-between cursor-pointer p-2 rounded border border-dashed ${darkMode ? 'border-gray-500 hover:bg-gray-600' : 'border-gray-300 hover:bg-blue-50'}`}
                            onClick={() => openStockItemSelectionModal(index)}
                          >
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>
                              {item.stockItemName || 'Click to select stock item'}
                            </span>
                            <FaSearch className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                          </div>
                          {item.stockItemDetails && (
                            <div className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ID: {item.stockItemDetails.id} | 
                              Unit: {item.stockItemDetails.unitOfMeasure} |
                              Brand: {item.stockItemDetails.brand?.name}
                            </div>
                          )}
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            ref={el => freshQuantityRefs.current[index] = el}
                            type="number"
                            value={item.freshQuantity}
                            onChange={(e) => handleStockChange(index, 'freshQuantity', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshQuantity')}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'border-gray-300'}`}
                            min="0"
                            max={item.availableStock.freshQuantity}
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            ref={el => damagedQuantityRefs.current[index] = el}
                            type="number"
                            value={item.damagedQuantity}
                            onChange={(e) => handleStockChange(index, 'damagedQuantity', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'damagedQuantity')}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'border-gray-300'}`}
                            min="0"
                            max={item.availableStock.damagedQuantity}
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <div className="text-green-600">Fresh: {item.availableStock.freshQuantity}</div>
                            <div className="text-red-600">Damaged: {item.availableStock.damagedQuantity}</div>
                          </div>
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className={`p-1 text-red-600 rounded ${darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-100'}`}
                            disabled={items.length === 1}
                          >
                            <FaTrash size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Item Button */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <FaPlus className="mr-2" />
                  Add Item
                </button>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Press F2 to select stock item | Enter to move to next field
                </span>
              </div>

              {/* Remarks */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaStickyNote className="inline mr-1" />
                  Remarks
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`}
                  rows="3"
                  placeholder="Enter any remarks about this transfer..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Transfer...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      Create Transfer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Transfer History Tab */}
        {activeTab === 'history' && (
          <div className={`rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* History Header */}
            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Transfer History</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    <FaDownload className="mr-2" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800"
                    title="Export to Excel with filters"
                  >
                    <FaFileExcel className="mr-2" />
                    Export Excel
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    <FaFilter className="mr-2" />
                    Filters
                  </button>
                </div>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className={`grid grid-cols-1 md:grid-cols-5 gap-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>From Location</label>
                    <Select
                      value={locations.find(l => l._id === historyFilters.fromLocation) ? {
                        value: historyFilters.fromLocation,
                        label: locations.find(l => l._id === historyFilters.fromLocation)?.name || locations.find(l => l._id === historyFilters.fromLocation)?.locationName
                      } : null}
                      onChange={(option) => handleHistoryFilterChange('fromLocation', option?.value || null)}
                      options={locations.map(location => ({
                        value: location._id,
                        label: location.name || location.locationName
                      }))}
                      placeholder="All locations"
                      isClearable
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>To Location</label>
                    <Select
                      value={locations.find(l => l._id === historyFilters.toLocation) ? {
                        value: historyFilters.toLocation,
                        label: locations.find(l => l._id === historyFilters.toLocation)?.name || locations.find(l => l._id === historyFilters.toLocation)?.locationName
                      } : null}
                      onChange={(option) => handleHistoryFilterChange('toLocation', option?.value || null)}
                      options={locations.map(location => ({
                        value: location._id,
                        label: location.name || location.locationName
                      }))}
                      placeholder="All locations"
                      isClearable
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                    <input
                      type="date"
                      value={historyFilters.startDate || ''}
                      onChange={(e) => handleHistoryFilterChange('startDate', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                    <input
                      type="date"
                      value={historyFilters.endDate || ''}
                      onChange={(e) => handleHistoryFilterChange('endDate', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'border-gray-300'}`}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={resetHistoryFilters}
                      className="flex items-center px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                      <FaTimes className="mr-1" />
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* History Table */}
            <div className="overflow-x-auto">
              {historyLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className={`ml-2 ${darkMode ? 'text-gray-300' : ''}`}>Loading transfer history...</span>
                </div>
              ) : transferHistory.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No transfer records found
                </div>
              ) : (
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Transfer ID
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Date
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        From → To Location
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Items Summary
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Quantity
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Created By
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {transferHistory.map((transfer) => (
                      <tr key={transfer._id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {transfer.transferId}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatDate(transfer.transferDate)}
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-800'}`}>
                              {transfer.fromLocationName}
                            </span>
                            <FaArrowRight className={`mx-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                            <span className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-800'}`}>
                              {transfer.toLocationName}
                            </span>
                          </div>
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div className="space-y-1">
                            <div>{transfer.summary?.totalItems || transfer.items?.length || 0} items</div>
                            {transfer.summary && (
                              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                Fresh: {transfer.summary.totalFreshQuantity} | 
                                Damaged: {transfer.summary.totalDamagedQuantity}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">
                            {transfer.summary?.totalQuantity || 
                             (transfer.items?.reduce((sum, item) => sum + (item.freshQuantity || 0) + (item.damagedQuantity || 0), 0) || 0)}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div>
                            {transfer.createdByName || transfer.createdBy?.name || 'Unknown'}
                          </div>
                          {transfer.createdBy?.email && (
                            <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{transfer.createdBy.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(transfer)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <FaEye className="mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`px-6 py-3 flex items-center justify-between border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                    disabled={historyPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setHistoryPage(Math.min(totalPages, historyPage + 1))}
                    disabled={historyPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Showing{' '}
                      <span className="font-medium">{((historyPage - 1) * historyLimit) + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(historyPage * historyLimit, totalHistory)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{totalHistory}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setHistoryPage(1)}
                        disabled={historyPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        First
                      </button>
                      <button
                        onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                        disabled={historyPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {/* Page numbers */}
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        const pageNumber = Math.max(1, Math.min(totalPages - 4, historyPage - 2)) + index;
                        if (pageNumber <= totalPages) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => setHistoryPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNumber === historyPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : darkMode
                                    ? 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => setHistoryPage(Math.min(totalPages, historyPage + 1))}
                        disabled={historyPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaChevronRight />
                      </button>
                      <button
                        onClick={() => setHistoryPage(totalPages)}
                        disabled={historyPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        Last
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stock Items Selection Modal */}
        <StockItemSelectionModal
          isOpen={showStockItemsModal}
          onClose={() => { setShowStockItemsModal(false); setCurrentItemIndex(null); }}
          onSelect={handleStockItemSelect}
          darkMode={darkMode}
          userId={user?.user?._id}
          locationId={selectedFromLocation?.value || null}
        />

        {/* Details Modal */}
        {showDetailsModal && selectedTransfer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg shadow-xl w-11/12 md:w-4/5 lg:w-3/4 max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Transfer Details - {selectedTransfer.transferId}
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Transfer Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <div className="flex items-center">
                      <FaCalendarAlt className="text-blue-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Transfer Date</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>{formatDate(selectedTransfer.transferDate)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                    <div className="flex items-center">
                      <FaBox className="text-green-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedTransfer.summary?.totalItems || selectedTransfer.items?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                    <div className="flex items-center">
                      <FaBox className="text-yellow-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Quantity</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedTransfer.summary?.totalQuantity || 
                           selectedTransfer.items?.reduce((sum, item) => sum + (item.freshQuantity || 0) + (item.damagedQuantity || 0), 0) || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="text-purple-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Route</p>
                        <p className={`font-semibold text-xs ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedTransfer.fromLocationName} → {selectedTransfer.toLocationName}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transfer Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>From Location</label>
                      <div className={`mt-1 p-3 border rounded-md ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-800'}`}>{selectedTransfer.fromLocationName}</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>To Location</label>
                      <div className={`mt-1 p-3 border rounded-md ${darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-800'}`}>{selectedTransfer.toLocationName}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Created By</label>
                      <div className={`mt-1 p-3 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedTransfer.createdByName || selectedTransfer.createdBy?.name || 'Unknown'}
                        </p>
                        {selectedTransfer.createdBy?.email && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedTransfer.createdBy.email}</p>
                        )}
                      </div>
                    </div>
                    
                    {selectedTransfer.summary && (
                      <div>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Quantity Breakdown</label>
                        <div className={`mt-1 p-3 border rounded-md ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-green-600 font-medium">Fresh: </span>
                              <span className={darkMode ? 'text-gray-200' : ''}>{selectedTransfer.summary.totalFreshQuantity}</span>
                            </div>
                            <div>
                              <span className="text-red-600 font-medium">Damaged: </span>
                              <span className={darkMode ? 'text-gray-200' : ''}>{selectedTransfer.summary.totalDamagedQuantity}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTransfer.remarks && (
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remarks</label>
                    <div className={`p-3 border rounded-md ${darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedTransfer.remarks}</p>
                    </div>
                  </div>
                )}

                {/* Transferred Items Table */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Transferred Items Details</label>
                  <div className={`overflow-x-auto border rounded-lg ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Stock Item Details
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Brand & Category
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Fresh Qty
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Damaged Qty
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total Qty
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Unit
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                        {selectedTransfer.items?.map((item, index) => (
                          <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-4">
                              <div>
                                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {item.stockItemId?.name || 'N/A'}
                                </p>
                                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  ID: {item.stockItemId?.id || 'N/A'}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-1">
                                <div className="text-xs">
                                  <span className={`px-2 py-1 rounded ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-800'}`}>
                                    {item.stockItemId?.brand?.name || 'No Brand'}
                                  </span>
                                </div>
                                <div className="text-xs">
                                  <span className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                                    {item.stockItemId?.category?.name || 'No Category'}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-800'}`}>
                                {item.freshQuantity || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-800'}`}>
                                {item.damagedQuantity || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-800'}`}>
                                {(item.freshQuantity || 0) + (item.damagedQuantity || 0)}
                              </span>
                            </td>
                            <td className={`px-4 py-4 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.stockItemId?.unitOfMeasure || 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Items Summary Footer */}
                  {selectedTransfer.summary && (
                    <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items</p>
                          <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedTransfer.summary.totalItems}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Quantity</p>
                          <p className="text-lg font-semibold text-green-600">{selectedTransfer.summary.totalFreshQuantity}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Damaged Quantity</p>
                          <p className="text-lg font-semibold text-red-600">{selectedTransfer.summary.totalDamagedQuantity}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Quantity</p>
                          <p className="text-lg font-semibold text-blue-600">{selectedTransfer.summary.totalQuantity}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Excel Modal */}
        {showExportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className={`rounded-2xl shadow-2xl w-full max-w-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              {/* Header */}
              <div className={`px-6 py-4 border-b flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-green-50'}`}>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FaCalendarAlt className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Export Location Transfers</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Select date range and location</p>
                  </div>
                </div>
                <button onClick={() => setShowExportModal(false)} className={`p-2 rounded-full hover:bg-opacity-20 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-400 hover:bg-gray-100'}`}>
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                  <input
                    type="date"
                    value={exportFilters.startDate}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                  <input
                    type="date"
                    value={exportFilters.endDate}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location (Optional)</label>
                  <Select
                    placeholder="All Locations"
                    isClearable
                    value={exportFilters.location ? {
                      value: exportFilters.location,
                      label: locations.find(l => l._id === exportFilters.location)?.locationName || locations.find(l => l._id === exportFilters.location)?.name
                    } : null}
                    onChange={(option) => setExportFilters(prev => ({ ...prev, location: option?.value || null }))}
                    options={locations.map(loc => ({ value: loc._id, label: loc.locationName || loc.name }))}
                    styles={selectStyles}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className={`border-t px-6 py-4 flex justify-end space-x-3 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                <button
                  onClick={() => setShowExportModal(false)}
                  className={`px-4 py-2 rounded-lg ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExportExcel}
                  disabled={exportLoading || !exportFilters.startDate || !exportFilters.endDate}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="w-4 h-4" />
                      <span>Export Excel</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockItemsLocationTransfer;
